import { Box, PremiumFeature, Tooltip, TooltipContent, TooltipTrigger } from "@bcc-ui/ui-components";
import classNames from "classnames";
import React from "react";
import { NavLink } from "react-router-dom";

import { getAdvertisedRouteTooltipContent } from "./getAdvertisedRouteTooltipContent";
import style from "./navigation-sub-menu-item.scss";
import { isTextTruncated } from "utils/commonFunctions";

interface DataAttributes {
    ariaExpanded: boolean;
}

interface Props {
    to: string;
    children: React.ReactNode;
    leftSection?: React.ReactNode;
    rightSection?: React.ReactNode;
    isAdvertised?: boolean;
    asBox?: boolean;
    asExternalLink?: boolean;
    onClick?: () => void;
    testId?: string;
    dataAttributes?: DataAttributes;
    className?: string;
}

export const NavigationSubMenuItem = (props: Props) => {
    const classes = classNames(style.navigationMenuItem, props.className);
    const middleSectionRef = React.useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = React.useState(false);

    React.useLayoutEffect(() => {
        const element = middleSectionRef.current;
        if (!element) {
            return;
        }
        const update = () => setIsTruncated(isTextTruncated(element));
        update();
        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(element);
        return () => resizeObserver.disconnect();
    }, [props.children, props.rightSection]);

    const hasDefaultCursor = props.asBox && !props.onClick && !isTruncated;

    const middleSection = (
        <Tooltip placement="top">
            {isTruncated && <TooltipContent>{props.children}</TooltipContent>}
            <TooltipTrigger className={classNames(style.tooltipTrigger, hasDefaultCursor && style.defaultCursor)}>
                <span className={style.middleSection} ref={middleSectionRef}>
                    {props.children}
                </span>
            </TooltipTrigger>
        </Tooltip>
    );

    if (props.isAdvertised) {
        const tooltipContent = getAdvertisedRouteTooltipContent(props.to);

        return (
            <Tooltip placement="right">
                <TooltipContent>{tooltipContent}</TooltipContent>
                <TooltipTrigger>
                    <Box className={style.navigationMenuItem} data-testid={props.testId}>
                        <Box justifyContent="SpaceBetween" className={style.rightAndMiddleSections}>
                            <span className={classNames(style.middleSection, style.disabled)}>{props.children}</span>

                            <PremiumFeature size={20} color={"--app-subNavigationPanel-iconColor"} />
                        </Box>
                    </Box>
                </TooltipTrigger>
            </Tooltip>
        );
    }

    if (props.asBox) {
        return (
            <Box
                onClick={props.onClick}
                data-testid={props.testId}
                aria-expanded={props.dataAttributes && props.dataAttributes.ariaExpanded}
                className={classes}
            >
                {props.leftSection && <span>{props.leftSection}</span>}

                <Box justifyContent="SpaceBetween" className={style.rightAndMiddleSections}>
                    {middleSection}
                    {props.rightSection && <span className={style.rightSection}>{props.rightSection}</span>}
                </Box>
            </Box>
        );
    }

    if (props.asExternalLink) {
        return (
            <a href={props.to} target="_blank" rel="noreferrer" data-testid={props.testId} className={classes}>
                {props.leftSection && <span>{props.leftSection}</span>}

                <Box justifyContent="SpaceBetween" className={style.rightAndMiddleSections}>
                    {middleSection}
                    {props.rightSection && <span className={style.rightSection}>{props.rightSection}</span>}
                </Box>
            </a>
        );
    }

    return (
        <NavLink to={props.to} exact data-testid={props.testId} className={classes}>
            {props.leftSection && <span>{props.leftSection}</span>}

            <Box justifyContent="SpaceBetween" className={style.rightAndMiddleSections}>
                {middleSection}
                {props.rightSection && <span className={style.rightSection}>{props.rightSection}</span>}
            </Box>
        </NavLink>
    );
};
